import React from 'react';
import { Row, Column } from 'react-foundation';
import styled from 'styled-components';
import _ from 'lodash';
import { Error } from '../../components/Error';
import { PageHeader } from '../../components/PageHeader';
import { BillingPeriodSelect } from '../../components/BillingPeriodSelect';
import NonActivatedSimsFeatures from '../SimsPage/NonActivatedSimsFeatures';
import { Consumption } from './Consumption';
import { CurrentOrderIndicator } from './CurrentOrderIndicator';
import { SimsIndicator } from './SimsIndicator';
import { ServiceExpenses } from './ServiceExpenses';
import { someEndpointCallFailed } from '../../utils/helpers';
import { OfferingType } from '../../utils/offeringType';

interface Props {
	accountType: string;
	billingPeriods: any[];
	activeBillingPeriod: any;
	updateBillingPeriod: (data: { startDate: string; endDate: string }) => void;
	nonActivatedSims: any[];
	errors: any[];
	usageFetching: boolean;
	// TODO more specific parameter types
	usage: any;
	dataPerSim: any;
	moisPerSim: any;
	dataTotal: any;
	simsCount: any;
	moisTotal: any;
	getWarningValue: any;
	getMessageAndVoiceUsage: any;
	getEstimate: any;
	getMaxValue: any;
	getDataUsage: any;
	price: any;
	currentOrder: any;
	isUnlimited: any;
	emptySims: any;
	sims: any;
	offeringType: OfferingType;
}

const SelectContainer = styled.div`
	float: right;
	max-width: 15.5rem;
	margin-left: 1rem;
	margin-top: 2rem;
	max-height: 2rem;
	width: 100%;
	text-align: center;
`;

const Section = styled.div`
	margin-top: 2rem;
`;

const Container = styled.div`
	margin: auto;
	width: 85%;
`;

const hasData = (props: any) => _.every([props.currentOrder, props.billingPeriods, props.emptySims, props.sims, props.usage], (prop) => prop !== null);

const canShowUsage = (props: any) =>
	!props.usageFetching && hasData(props) && !someEndpointCallFailed(props.errors, ['detailed-usages', 'billing-periods', 'order', 'status=active', 'sims/empty/account']);

const canShowNonActivatedSims = (props: any) => props.nonActivatedSims && props.nonActivatedSims.length > 0 && props.accountType === 'consumer';

const shouldShowErrorMessage = (props: any) =>
	!canShowUsage(props) &&
	!canShowNonActivatedSims(props) &&
	(!props.usageFetching || someEndpointCallFailed(props.errors, ['detailed-usages'])) &&
	(!props.nonActivatedSimsFetching || someEndpointCallFailed(props.errors, ['status=new']));

export const DashboardContent: React.FC<Props> = (props: Props) => {
	const selectedBillingPeriod = props.activeBillingPeriod || (props.billingPeriods ? props.billingPeriods[0] : undefined);
	return (
		<div>
			<Container>
				<NonActivatedSimsFeatures nonActivatedSims={props.nonActivatedSims} offeringType={props.offeringType} />
			</Container>
			{props.usageFetching && !someEndpointCallFailed(props.errors, ['detailed-usages']) && <PageHeader>Haetaan kulutustietoja...</PageHeader>}
			{canShowUsage(props) && (
				<div>
					<SelectContainer>
						<BillingPeriodSelect billing={props.billingPeriods} selectedBillingPeriod={selectedBillingPeriod} sendPeriod={props.updateBillingPeriod} />
					</SelectContainer>
					<Column className="large-10 large-centered columns">
						<PageHeader>Kulutus</PageHeader>
						<Row>
							<Consumption
								takeFullWidth={props.accountType === 'consumer'}
								simpleDials={props.accountType === 'consumer'}
								usage={props.usage}
								dataPerSim={props.dataPerSim}
								moisPerSim={props.moisPerSim}
								dataTotal={props.dataTotal}
								simsCount={props.simsCount}
								moisTotal={props.moisTotal}
								getWarningValue={props.getWarningValue}
								getMessageAndVoiceUsage={props.getMessageAndVoiceUsage}
								getEstimate={props.getEstimate}
								getMaxValue={props.getMaxValue}
								getDataUsage={props.getDataUsage}
								billingPeriod={selectedBillingPeriod}
							/>
						</Row>
						{props.accountType === 'business' && (
							<Row>
								<CurrentOrderIndicator dataPerSim={props.dataPerSim} moisPerSim={props.moisPerSim} price={props.price} currentOrder={props.currentOrder} isUnlimited={props.isUnlimited} />
								<SimsIndicator emptySims={props.emptySims} sims={props.sims} />
							</Row>
						)}
						<Section>
							<Row>
								<ServiceExpenses usage={props.usage} accountType={props.accountType} />
							</Row>
						</Section>
					</Column>
				</div>
			)}
			{shouldShowErrorMessage(props) && <Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />}
		</div>
	);
};
